import { useUpdateAuctionMutation } from "@/mutations/auction.ts";
import { useQueryOptionsFactory } from "@/queries";
import { defaultMutationErrorHandler } from "@/utils/api.ts";
import { zonedDateTimeToDayJs } from "@/utils/datetime.ts";
import { DateTimeFormatter } from "@js-joda/core";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import {
    type FormEventHandler,
    type ReactNode,
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";
import type { DefaultValues } from "react-hook-form";
import { match } from "ts-pattern";
import AuctionDialogRoot from "../-components/AuctionDialogRoot.tsx";
import AuctionForm, {
    type AuctionFieldValues,
    type AuctionTransformedValues,
} from "../-components/AuctionForm.tsx";

const Root = (): ReactNode => {
    const qof = useQueryOptionsFactory();
    const { auctionId } = Route.useParams();
    const auction = useSuspenseQuery(qof.auction.get(auctionId)).data;

    const onSubmitRef = useRef<FormEventHandler>(undefined);
    const [open, setOpen] = useState(true);
    const updateMutation = useUpdateAuctionMutation();

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const defaultValues = useMemo((): DefaultValues<AuctionFieldValues> => {
        const config: DefaultValues<AuctionFieldValues["config"]> = match(auction.config)
            .with({ type: "single_parcel" }, (config) => ({
                ...config,
                parcel: {
                    ...config.parcel,
                    acreage: config.parcel.acreage.toFixed(2, 0),
                    startingBid: config.parcel.startingBid?.toString() ?? "",
                    biddingIncrement: config.parcel.biddingIncrement?.toString() ?? "",
                },
            }))
            .with({ type: "multi_parcel" }, (config) => ({
                ...config,
                parcels: config.parcels.map((parcel, index) => ({
                    ...parcel,
                    id: `parcel-${index}`,
                    acreage: parcel.acreage.toFixed(2, 0),
                    startingBid: parcel.startingBid?.toString() ?? "",
                    biddingIncrement: parcel.biddingIncrement?.toString() ?? "",
                })),
            }))
            .with({ type: "combo" }, (config) => ({
                ...config,
                parcels: config.parcels.map((parcel, index) => ({
                    ...parcel,
                    id: `parcel-${index}`,
                    acreage: parcel.acreage.toFixed(2, 0),
                    startingBid: parcel.startingBid?.toString() ?? "",
                    biddingIncrement: parcel.biddingIncrement?.toString() ?? "",
                })),
                parcelCombinations: config.parcelCombinations.map((combination, index) => ({
                    ...combination,
                    id: `parcel-combination-${index}`,
                    startingBid: combination.startingBid?.toString() ?? "",
                    biddingIncrement: combination.biddingIncrement?.toString() ?? "",
                })),
            }))
            .with({ type: "choice" }, (config) => ({
                ...config,
                startingBid: config.startingBid?.toString() ?? "",
                biddingIncrement: config.biddingIncrement?.toString() ?? "",
                parcels: config.parcels.map((parcel, index) => ({
                    ...parcel,
                    id: `parcel-${index}`,
                    acreage: parcel.acreage.toFixed(2, 0),
                })),
            }))
            .exhaustive();

        return {
            ...auction,
            startsAt: zonedDateTimeToDayJs(auction.startsAt),
            pricePerAcre: auction.priceModel === "per_acre",
            config,
        };
    }, [auction]);

    const handleSubmit = useCallback(
        (values: AuctionTransformedValues) => {
            updateMutation.mutate(
                {
                    id: auction.id,
                    ...values,
                    startsAt: values.startsAt.format(DateTimeFormatter.ISO_OFFSET_DATE_TIME),
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Auction has been updated", { variant: "success" });
                        setOpen(false);
                    },
                    onError: defaultMutationErrorHandler,
                },
            );
        },
        [auction.id, updateMutation],
    );

    return (
        <AuctionDialogRoot
            open={open}
            onClose={handleClose}
            maxWidth="md"
            onSubmitRef={onSubmitRef}
        >
            <AuctionForm
                title="Edit Auction"
                type={auction.type}
                sealedBid={auction.sealedBid}
                defaultValues={defaultValues}
                isEdit
                onClose={handleClose}
                onSubmit={handleSubmit}
                onSubmitRef={onSubmitRef}
            />
        </AuctionDialogRoot>
    );
};

export const Route = createFileRoute("/manage/auctions/$auctionId/edit")({
    component: Root,
});
